import { defineStore } from 'pinia';
import { reactive, toRefs } from 'vue';
import type { HipSpineStudyApiObject } from '@/api/hip-spine-study/hipSpineStudy';
import type { HipSpinePostureApiObject } from '@/api/hip-spine-study/hipSpinePosture';
import type { HipSpineApprovedLandmarkApiObject } from '@/api/hip-spine-study/hipSpineLandmark';
import type { HipSpinePostureViewState, SafeZoneVisibility } from './types';
import { createHipSpineStoreGetters } from './hipSpineStoreGetters';
import { createHipSpineStoreActions } from './hipSpineStoreActions';

export type { HipSpinePostureViewState };

export type PanelView = 'angles' | 'annotations';

export type StoreState = {
    caseId: number | null;
    studyId: number | null;
    hipSpineStudy: HipSpineStudyApiObject | null;
    safeZoneVisibility: SafeZoneVisibility;
    isSafeZoneSliderMode: boolean;
    isLoading: boolean;
    loadError: string | null;
    hipSpinePostures: HipSpinePostureApiObject[];
    approvedLandmarks: HipSpineApprovedLandmarkApiObject[];
    activePostureId: string | null;
    activePanelView: PanelView;
};

/**
 * Shared context the extracted getters/actions operate on. The mutable `abort`
 * holder replaces the former `let abortController` closure variable so actions
 * split into a separate module can still read and replace the live controller.
 */
export type HipSpineStoreContext = {
    state: StoreState;
    postureViewStates: Map<string, HipSpinePostureViewState>;
    abort: { controller: AbortController | null };
};

export function initialState(): StoreState {
    return {
        caseId: null,
        studyId: null,
        hipSpineStudy: null,
        safeZoneVisibility: 'hide',
        isSafeZoneSliderMode: false,
        isLoading: false,
        loadError: null,
        hipSpinePostures: [],
        approvedLandmarks: [],
        activePostureId: null,
        activePanelView: 'annotations',
    };
}

export const useHipSpineStore = defineStore('hip-spine', () => {
    const state = reactive(initialState());
    // The scene's `sceneState` (rendering/camera/nodes/imageAdjustments) is now
    // owned by `useHipSpineXRaySceneViewModel`, per docs/threejs-practices.md
    // ("scene state belongs in a ViewModel store, not a Model store").
    // TODO(scene-state-split): the scene-shaped fields *inside* each
    // `postureViewStates` entry (annotationGroups, angleGroups, angles,
    // imageMaterial) still live here — `rebuildPostureGeometry` mutates them in
    // place. Raw `landmarks` is correctly Model-owned. Move the scene fields onto
    // the scene VM (keyed by posture) so this map holds only domain data.
    const postureViewStates = reactive(new Map<string, HipSpinePostureViewState>());
    const ctx: HipSpineStoreContext = {
        state,
        postureViewStates,
        abort: { controller: null },
    };

    return {
        ...toRefs(state),
        postureViewStates,
        ...createHipSpineStoreGetters(ctx),
        ...createHipSpineStoreActions(ctx),
    };
});
